import { hidePurchases } from './native'

/**
 * The arithmetic behind every limit meter (v0.25.0, D-144) — pure, so the rules
 * that decide when to warn someone can be tested without a browser.
 *
 * `LimitMeter.tsx` renders these; nothing here knows about React.
 */

export type LimitKey = 'trees' | 'storage' | 'sage' | 'tickets'

export interface Limit {
  key: LimitKey
  label: string
  used: number
  limit: number
  unit: 'count' | 'bytes' | 'messages'
  extra?: Record<string, number | string>
}

const MB = 1024 * 1024
export const mb = (b: number) => (b >= 1024 * MB ? `${(b / 1024 / MB).toFixed(1)} GB` : `${Math.round(b / MB)} MB`)

/** Largest single file each plan will accept — the storage band is derived from it. */
const LARGEST_UPLOAD: Record<string, number> = { seedling: 30 * MB, heritage: 95 * MB, dynasty: 95 * MB }

export type Tone = 'quiet' | 'warn' | 'full'

export function toneFor(l: Limit, plan = 'seedling'): Tone {
  if (l.limit < 0) return 'quiet'
  const remaining = l.limit - l.used
  if (remaining <= 0) return 'full'
  if (l.unit === 'bytes') return remaining < 2 * (LARGEST_UPLOAD[plan] ?? LARGEST_UPLOAD.seedling) ? 'warn' : 'quiet'
  // Counted limits: three-quarters is right for a big allowance, but on a cap of
  // five it warns with a single slot left, which is barely a warning. So warn
  // whenever two or fewer remain as well — and only on caps big enough for that
  // to mean anything, or a one-tree plan would nag someone who owns no trees.
  const ratio = l.used / l.limit
  return ratio >= 0.75 || (l.limit >= 4 && remaining <= 2) ? 'warn' : 'quiet'
}

/** Plain-language "what's left", per limit. */
export function remainingText(l: Limit): string {
  const left = Math.max(0, l.limit - l.used)
  if (l.unit === 'bytes') return `${mb(left)} left of ${mb(l.limit)}`
  if (l.key === 'sage') return `${left} of ${l.limit} Sage messages left this month`
  if (l.key === 'trees') return `${l.used} of ${l.limit} constellation${l.limit === 1 ? '' : 's'}`
  return `${l.used} of ${l.limit} open reports`
}

/** What the person can actually DO — never a checkout that cannot take money. */
export function remedyFor(l: Limit, purchasesEnabled: boolean): string {
  const buy = purchasesEnabled && !hidePurchases()
  switch (l.key) {
    case 'storage': {
      // Name the amount, not just the mechanism. "Free up some space" is the advice
      // that sent people to a Trash which, until v0.26.0, never actually gave any
      // back — so if there is something reclaimable, say how much (D-146).
      const trash = Number(l.extra?.trash_bytes ?? 0)
      const lever = trash > 0
        ? `${mb(trash)} is sitting in the Trash — ⚙ → Trash → Empty gives it back straight away.`
        : 'Deleting a photo moves it to the Trash; emptying that (⚙ → Trash) is what returns the space.'
      return `${lever}${buy ? ' You can also add a storage pack from Profile.' : ' You can also ask the keeper for more room.'}`
    }
    case 'sage':
      return `Your allowance renews on the 1st${buy ? ', or top up from Profile → Sage Credits' : ' — nothing else in the archive is affected in the meantime'}.`
    case 'trees':
      // name the buttons, not the codename — see trees/index.ts
      return 'If these are more of the same family, add them to the constellation you already have and use the view buttons above it (Everyone · Close family · My ancestors …) to show one branch at a time. If it is a separate family, ask the keeper for room via Support.'
    case 'tickets':
      return 'This clears on its own as older reports age out — and reports about content or Sage are never held back.'
  }
}
